import {
    EntityViewContent,
    EntityViewHeader,
} from '@/components/common/EntityView';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import { Subheader } from '@/components/common/Typography';
import { useMutation, useQuery } from '@apollo/client';
import { FlexProps } from '@chakra-ui/react';
import { assert } from '@common/errors';
import {
    GoalAccountOnboardingDocument,
    UpdateGoalAccountOnboardingDocument,
} from '@common/graphql/generated';
import {
    IraType,
    RetirementAccountOnboardingInfo,
    RetirementAccountOnboardingStep,
} from '@common/types';
import { createContext, useCallback, useContext, useState } from 'react';
import GoalIconCircle from '../GoalIconCircle';
import GoalOnboardingCelebrationView from '../GoalOnboardingCelebrationView';
import { labelByIraType } from './helpers';
import OpenIraFlowBeneficiaryStep from './OpenIraFlowBeneficiaryStep';
import OpenIraFlowBreadcrumbs from './OpenIraFlowBreadcrumbs';
import OpenIraFlowFundingStep from './OpenIraFlowFundingStep';
import OpenIraFlowIntroStep from './OpenIraFlowIntroStep';
import OpenIraFlowPlanStep from './OpenIraFlowPlanStep';
import OpenIraFlowPortfolioStep from './OpenIraFlowPortfolioStep';
import OpenIraFlowReviewStep from './OpenIraFlowReviewStep';
import OpenIraFlowTermsStep from './OpenIraFlowTermsStep';
import { useRetirementGoalContext } from './RetirementGoalDetailContainer';

type UpdateInfoFn = (info: RetirementAccountOnboardingInfo) => Promise<void>;

interface OpenIraFlowContext {
    iraType: IraType;
    step: RetirementAccountOnboardingStep;
    setStep: (step: RetirementAccountOnboardingStep) => void;
    info: RetirementAccountOnboardingInfo | undefined;
    updateInfo: UpdateInfoFn;
    exitFlow: VoidFunction;
    accountOnboardingId: string | undefined;
    setAccountOnboardingId: (accountOnboardingId: string) => void;
}

export const OpenIraFlowContext = createContext<OpenIraFlowContext | null>(
    null
);

export const useOpenIraFlowContext = (): OpenIraFlowContext => {
    const ctx = useContext(OpenIraFlowContext);
    assert(ctx, 'Value must be passed to OpenIraFlowContext');
    return ctx;
};

export interface OpenIraFlowProps extends FlexProps {
    iraType: IraType;
}

export const OpenIraFlow = ({ iraType, ...props }: OpenIraFlowProps) => {
    const { goal, setPosition } = useRetirementGoalContext();
    const [step, setStep] = useState<RetirementAccountOnboardingStep>('INTRO');

    const [accountOnboardingId, setAccountOnboardingId] = useState(
        goal.goalAccountOnboardings.find(
            (o) => o.isMine && o.info.iraType === iraType
        )?.id
    );

    const [updateGoalAccountOnboarding] = useMutation(
        UpdateGoalAccountOnboardingDocument
    );

    const { data } = useQuery(GoalAccountOnboardingDocument, {
        variables: { id: accountOnboardingId ?? '' },
        skip: !accountOnboardingId,
    });

    const updateInfo: UpdateInfoFn = useCallback(
        async (updatedInfo: RetirementAccountOnboardingInfo) => {
            assert(accountOnboardingId);
            await updateGoalAccountOnboarding({
                variables: { id: accountOnboardingId, info: updatedInfo },
            });
        },
        [accountOnboardingId, updateGoalAccountOnboarding]
    );

    const exitFlow = () => setPosition('MAIN');

    if (
        data?.goalAccountOnboarding?.confirmedAt &&
        step !== 'TERMS' &&
        step !== 'CELEBRATION'
    ) {
        console.warn('Account is already confirmed; exiting flow', {
            data,
            step,
        });
        exitFlow();
        return <></>;
    }

    const info =
        data?.goalAccountOnboarding?.info.infoType === 'RETIREMENT'
            ? data.goalAccountOnboarding.info
            : undefined;

    return (
        <OpenIraFlowContext.Provider
            value={{
                iraType,
                step,
                setStep,
                info,
                updateInfo,
                exitFlow,
                accountOnboardingId,
                setAccountOnboardingId,
            }}
        >
            <FlexCol w="full" h="full" overflow="hidden" {...props}>
                <EntityViewHeader>
                    <FlexRow alignItems="center">
                        <GoalIconCircle type={goal.type} mr={3} />
                        <Subheader>{goal.name}</Subheader>
                    </FlexRow>
                    <OpenIraFlowBreadcrumbs />
                </EntityViewHeader>
                <EntityViewContent>
                    {
                        {
                            INTRO: <OpenIraFlowIntroStep />,
                            BENEFICIARY: <OpenIraFlowBeneficiaryStep />,
                            PORTFOLIO: <OpenIraFlowPortfolioStep />,
                            PLAN: <OpenIraFlowPlanStep />,
                            FUNDING: <OpenIraFlowFundingStep />,
                            REVIEW: <OpenIraFlowReviewStep />,
                            TERMS: <OpenIraFlowTermsStep />,
                            CELEBRATION: (
                                <GoalOnboardingCelebrationView
                                    goal={goal}
                                    subtitle={`Your Plenty ${labelByIraType[iraType]} IRA is getting set up in your Retirement goal.`}
                                    onComplete={() => {
                                        setPosition('MAIN');
                                    }}
                                />
                            ),
                        }[step]
                    }
                </EntityViewContent>
            </FlexCol>
        </OpenIraFlowContext.Provider>
    );
};
